import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import {
  selectAreCloneableItemsSelected,
  selectAreVersionableItemsSelected,
  selectIsDeleteDisabled,
  selectIsPublishDisabled,
  selectIsPublishHidden,
  selectSelectedResults,
  selectUserVmForDocumentList,
} from '@newStore/documentList/newDocumentListSelectors';
import {
  createCopy,
  createNewVersion,
  openCreateContentModal,
  openPublishModal,
  updateNews,
  validateAndDeleteSelected,
} from '@newStore/documentList/newDocumentListState';
import { selectHasUserUpdateNewsAbility } from '@newStore/user/userSelectors';
import { NodeType } from '@nodeTypeConfig/configTypes';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../hooks/useTypedSelector';

const DocumentListHeader: React.FC = () => {
  const dispatch = useDispatch();
  
  // Get data from Redux state
  const selected = useTypedSelector(selectSelectedResults);
  const deleteDisabled = useTypedSelector(selectIsDeleteDisabled);
  const deleting = useTypedSelector((state) => state.newDocumentList.deleting);
  const creatingNewVersion = useTypedSelector((state) => state.newDocumentList.creatingNewVersion);
  const creatingCopy = useTypedSelector((state) => state.newDocumentList.creatingCopy);
  const security = useTypedSelector(selectUserVmForDocumentList);
  const updateNewsDisabled = !useTypedSelector(selectHasUserUpdateNewsAbility);
  const cloneableItemSelected = useTypedSelector(selectAreCloneableItemsSelected);
  const allowVersionItemSelected = useTypedSelector(selectAreVersionableItemsSelected);
  const publishDisabled = useTypedSelector(selectIsPublishDisabled);
  const publishHidden = useTypedSelector(selectIsPublishHidden);

  const confirmationService = getAngularService('ConfirmationService');
  const modalWindowService = getAngularService('ModalWindowService');

  const handlePublish = async () => {
    const opts = {
      template: require('../../../../screen/modals/publishDate/publishDate.html'),
      controller: 'publishDateModal as ctrl',
    };

    dispatch(openPublishModal());
    await modalWindowService.open(opts);
  };

  const handleDelete = async () => {
    const confirmed = await confirmationService.confirmDeletion();
    if (confirmed) {
      dispatch(validateAndDeleteSelected());
    }
  };

  const handleCopy = async () => {
    const confirmed = await confirmationService.confirmCopy();
    if (confirmed) {
      dispatch(createCopy(selected));
    }
  };

  const handleDuplicate = async () => {
    const confirmed = await confirmationService.confirmVersion();
    if (confirmed) {
      dispatch(createNewVersion(selected));
    }
  };

  const handleCreate = async (type: NodeType) => {
    dispatch(openCreateContentModal({ type }));
    await modalWindowService.open({ component: 'createContentModal' });
  };

  const handleUpdateNews = async () => {
    const confirmed = await confirmationService.confirm({
      confirmationMessage: 'edit.updateNews.confirmation',
    });
    if (confirmed) {
      dispatch(updateNews());
    }
  };

  const selectedCount = selected.length;

  return (
    <header className="navbar navbar-default">
      <div className="navbar-left">
        <div className="navbar-controls">
          <div className="navbar-text navbar-text-big">Documenten</div>
        </div>
      </div>

      <div className="navbar-right">
        <div className="navbar-controls" style={{ display: 'flex', alignItems: 'center', height: '47px' }}>
          <div>
            <span>{selectedCount}</span> {selectedCount === 1 ? 'document' : 'documenten'} geselecteerd.
          </div>

          {!publishHidden && (
            <button
              className="btn btn-default"
              onClick={handlePublish}
              disabled={publishDisabled}
            >
              <span>Publiceren</span>
            </button>
          )}

          <button
            id="deleteButton"
            className="btn btn-default buttonRelative"
            onClick={handleDelete}
            disabled={deleteDisabled}
          >
            <div className={deleting ? 'invisible' : ''}>
              <span className="glyphicon glyphicon-delete" />
              <span> Verwijderen</span>
            </div>
            <div className={!deleting ? 'invisible' : 'spinner-icon center'} />
          </button>

          <button
            className="btn btn-default buttonRelative"
            onClick={handleCopy}
            disabled={!cloneableItemSelected}
          >
            <div className={creatingCopy ? 'invisible' : ''}>
              <span className="glyphicon glyphicon-duplicate" />
              <span> Kopiëren</span>
            </div>
            <div className={!creatingCopy ? 'invisible' : 'spinner-icon center'} />
          </button>

          <button
            className="btn btn-default buttonRelative"
            onClick={handleDuplicate}
            disabled={!allowVersionItemSelected}
          >
            <div className={creatingNewVersion ? 'invisible' : ''}>
              <span className="glyphicon glyphicon-duplicate" />
              <span> Nieuwe versie</span>
            </div>
            <div className={!creatingNewVersion ? 'invisible' : 'spinner-icon center'} />
          </button>

          <div className="dropdown">
            <a data-toggle="dropdown" className="dropdown-toggle btn btn-primary">
              <span className="glyphicon glyphicon-add" />
              <span className="btn-label"> Nieuw</span>
              <b className="caret" />
            </a>
            <ul className="dropdown-menu dropdown-menu-right">
              {security.creatables.map((creatable) => (
                <li key={creatable.type}>
                  <a onClick={() => handleCreate(creatable.type)}>
                    {creatable.information.single}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="dropdown">
            <button
              data-toggle="dropdown"
              className="dropdown-toggle btn btn-default"
              disabled={updateNewsDisabled}
            >
              <span className="glyphicon glyphicon-option-vertical" />
            </button>
            <ul className="dropdown-menu dropdown-menu-right">
              <li>
                <a onClick={handleUpdateNews}>
                  <span className="glyphicon glyphicon-open" />
                  <span> Nieuws bijwerken</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DocumentListHeader;
